<template>
    <div class="px-3 sm:px-16 lg:px-28 py-16 lg:py-24 text-[var(--light-gray)]">
        <!-- Form -->
        <div v-if="!result" class="grid lg:grid-cols-2 gap-12 lg:gap-20 max-w-screen-xl mx-auto">
            <div class="min-w-0">
                <h1 class="font-semibold">Speed up your integration roadmap</h1>
                <p class="mt-6 text-subtitle">
                    Unified maintains every integration for you and flags issues before your customers see them. See how it
                    works with your stack.
                </p>
                <h2 class="mt-10 text-xl font-semibold">On the call, we'll:</h2>
                <ul class="mt-4 space-y-3 text-[var(--medium-gray)]">
                    <li v-for="item in CALL_AGENDA" :key="item" class="flex gap-x-3">
                        <Icon name="material-symbols:check" class="h-6 w-5 flex-none text-blue-400" aria-hidden="true" />
                        <span>{{ item }}</span>
                    </li>
                </ul>
            </div>

            <form class="min-w-0 space-y-6" novalidate @submit.prevent="submit">
                <div>
                    <label for="bad-name" class="block font-semibold mb-2">Full name</label>
                    <input id="bad-name" v-model="form.name" type="text" autocomplete="name" required maxlength="200" :class="INPUT_CLASS" />
                    <p v-if="errors.name" class="mt-2 text-sm text-red-400">{{ errors.name }}</p>
                </div>

                <div>
                    <label for="bad-email" class="block font-semibold mb-2">Work email</label>
                    <input id="bad-email" v-model="form.email" type="email" autocomplete="email" required maxlength="320" :class="INPUT_CLASS" />
                    <p v-if="errors.email" class="mt-2 text-sm text-red-400">{{ errors.email }}</p>
                </div>

                <div>
                    <label for="bad-website" class="block font-semibold mb-2">
                        Company website (if different from your email domain)
                        <span class="font-normal text-[var(--medium-gray)]">(optional)</span>
                    </label>
                    <input id="bad-website" v-model="form.company_website" type="url" inputmode="url" autocomplete="url" maxlength="500"
                        placeholder="https://" :class="INPUT_CLASS" />
                </div>

                <fieldset>
                    <legend class="block font-semibold mb-2">Who's it for?</legend>
                    <div class="grid sm:grid-cols-2 gap-3">
                        <button v-for="option in AUDIENCES" :key="option.value" type="button" :aria-pressed="form.audience === option.value"
                            class="rounded-lg border px-4 py-3 text-left transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--secondary-blue)]"
                            :class="form.audience === option.value ? 'ring-2 ring-[var(--primary-blue)] bg-[var(--dark-gray)]' : 'hover:bg-[var(--dark-gray)]'"
                            @click="form.audience = option.value">
                            <span class="block font-semibold">{{ option.label }}</span>
                            <span class="block mt-1 text-sm text-[var(--medium-gray)]">{{ option.helper }}</span>
                        </button>
                    </div>
                    <p v-if="errors.audience" class="mt-2 text-sm text-red-400">{{ errors.audience }}</p>
                </fieldset>

                <div>
                    <label for="bad-notes" class="block font-semibold mb-2">
                        Anything else? <span class="font-normal text-[var(--medium-gray)]">(optional)</span>
                    </label>
                    <textarea id="bad-notes" v-model="form.notes" rows="4" maxlength="5000" :class="INPUT_CLASS" />
                </div>

                <!-- Honeypot: hidden from people, filled by bots -->
                <div class="hidden" aria-hidden="true">
                    <label for="bad-fax">Fax number</label>
                    <input id="bad-fax" v-model="form.fax_number" type="text" name="fax_number" tabindex="-1" autocomplete="off" />
                </div>

                <p v-if="errors.form" class="text-sm text-red-400" role="alert">{{ errors.form }}</p>

                <button type="submit" class="btn btn-primary w-full sm:w-auto flex items-center justify-center gap-2" :disabled="submitting"
                    :aria-busy="submitting">
                    <Icon v-if="submitting" name="material-symbols:progress-activity" class="text-lg animate-spin" aria-hidden="true" />
                    Continue
                </button>

                <p class="text-sm text-[var(--medium-gray)]">
                    Already a customer? <a href="/contact" class="text-blue-500 hover:text-blue-400">Get support</a>
                    &middot;
                    Just exploring? <a href="https://docs.unified.to" class="text-blue-500 hover:text-blue-400">Read the docs</a>
                </p>
            </form>
        </div>

        <!-- Calendar (MQL: Priority, existing accounts) -->
        <section v-else-if="result.state === 'calendar'" class="max-w-screen-lg mx-auto">
            <h1 class="font-semibold text-center">Pick a time with our team</h1>
            <iframe v-if="calendlySrc" :src="calendlySrc" title="Pick a time with our team" class="mt-8 w-full h-[1100px] sm:h-[760px] rounded-lg"
                frameborder="0" />
        </section>

        <!-- Getting Started (MQL: Standard, Not MQL, Unscored, spam) -->
        <section v-else-if="result.state === 'getting_started'" class="max-w-screen-md mx-auto">
            <h1 class="font-semibold">Thanks, your request is in.</h1>

            <h2 class="mt-12 text-2xl font-semibold">Try Unified first</h2>
            <p class="mt-4 text-subtitle">The fastest way to evaluate Unified is to build with it.</p>

            <p class="mt-8 font-semibold">Have your coding agent build it. Paste this into Claude Code, Cursor, Codex or Copilot:</p>
            <div class="mt-4 flex items-center gap-4 bg-gray-800 shadow-lg rounded-lg px-4 py-3">
                <code class="flex-1 min-w-0 break-words font-mono text-sm text-green-300">{{ AGENT_PROMPT }}</code>
                <button type="button" title="Copy prompt" class="flex-none text-gray-50" @click="copyPrompt">
                    <Icon :name="promptCopied ? 'material-symbols:check-small-rounded' : 'material-symbols:content-copy-outline-rounded'"
                        class="text-lg hover:text-green-500" />
                </button>
            </div>
            <p class="mt-4 text-[var(--medium-gray)]">
                Your agent plans the integration for your approval, activates it in the Sandbox with mock data and makes the first
                API call. No account yet? Your agent can create a {{ TRIAL_DAYS }}-day Sandbox workspace for you to claim.
            </p>

            <div class="mt-8 flex flex-wrap gap-4">
                <Button href="https://app.unified.to/" title="Start free trial" :full-width-mobile="true" />
                <Button href="/agents" title="Build with agents" variant="secondary" :full-width-mobile="true" />
                <Button href="https://docs.unified.to" title="Read the docs" variant="secondary" icon="ic:round-keyboard-arrow-right"
                    :full-width-mobile="true" />
            </div>

            <div v-if="result.show_startup_offer" class="mt-12 rounded-lg border border-[var(--border-gray)] bg-[var(--dark-gray)] p-6">
                <h3 class="text-xl font-semibold">Building a startup?</h3>
                <p class="mt-3 text-[var(--medium-gray)]">
                    We offer discounted pricing for eligible startups. We also provide exclusive perks for startups affiliated with
                    select accelerators in our Startup Program, including Techstars, Antler, DMZ, and Plug and Play. If your
                    accelerator isn't listed, <a href="/contact" class="text-blue-500 hover:text-blue-400">contact us</a> to nominate
                    them for our program.
                </p>
            </div>
        </section>

        <!-- Internal ("For my own team", or flagged internal use) -->
        <section v-else class="max-w-screen-md mx-auto">
            <h1 class="font-semibold">Thanks, your request is in.</h1>
            <h2 class="mt-12 text-2xl font-semibold">Two ways to connect your company's own apps</h2>
            <div class="mt-8 grid sm:grid-cols-2 gap-6">
                <div class="rounded-lg border border-[var(--border-gray)] bg-[var(--dark-gray)] p-6 flex flex-col">
                    <h3 class="text-xl font-semibold">UnifiedMCP</h3>
                    <p class="mt-3 flex-1 text-[var(--medium-gray)]">
                        Give ChatGPT, Claude and Gemini access to your team's SaaS accounts, with admin controls on what each
                        assistant can do.
                    </p>
                    <div class="mt-6"><Button href="https://unifiedmcp.com/" title="Start for free" /></div>
                </div>
                <div class="rounded-lg border border-[var(--border-gray)] bg-[var(--dark-gray)] p-6 flex flex-col">
                    <h3 class="text-xl font-semibold">UnifiedData.io</h3>
                    <p class="mt-3 flex-1 text-[var(--medium-gray)]">Move data between the apps your company already uses, with no code.</p>
                    <div class="mt-6"><Button href="https://unifieddata.io/" title="See UnifiedData.io" variant="secondary" /></div>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section v-if="!result" class="max-w-screen-xl mx-auto mt-24">
            <h2 class="text-2xl font-bold text-white border-b border-gray-700 pb-4">Frequently Asked Questions</h2>
            <Accordion v-model="activeFaq" :labels="BOOK_A_DEMO_FAQS.map((faq) => faq.title)" class="mt-8">
                <template v-for="(faq, index) of BOOK_A_DEMO_FAQS" :key="index" v-slot:[faqSlot(index)]>
                    <span v-html="faq.answer" class="text-gray-400" />
                </template>
            </Accordion>
        </section>
    </div>
</template>

<script setup lang="ts">
import { BOOK_A_DEMO_FAQS } from '~/constants/book-a-demo-faq';
import { TRIAL_DAYS } from '~/constants/pricing-config';
import { capture } from '~/services/analytics';

type TAudience = 'product' | 'internal';
interface IBookADemoResponse {
    state: 'calendar' | 'getting_started' | 'internal';
    calendly_url?: string;
    show_startup_offer: boolean;
}

const WORK_EMAIL_ERROR = 'Please use your work email.';
const AGENT_PROMPT = 'Add Unified.to integrations to my app: https://unified.to/SKILL.md';
const INPUT_CLASS =
    'w-full rounded-lg border border-[var(--border-gray)] bg-[var(--dark-gray)] px-4 py-3 text-base text-[var(--light-gray)] placeholder:text-[var(--medium-gray)] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--secondary-blue)]';
const CALL_AGENDA = [
    'Understand your product and integration requirements',
    'Show how to activate integrations, connect accounts and test data from a connected account',
    'Clarify what Unified handles and what your team owns',
    'Confirm feasibility, timelines and your path to launch',
];
const AUDIENCES: { value: TAudience; label: string; helper: string }[] = [
    { value: 'product', label: 'For my product', helper: 'Your customers connect their accounts' },
    { value: 'internal', label: 'For my own team', helper: "Connecting your company's own apps" },
];

useSEO({
    title: 'Book a demo',
    description:
        'See how Unified works with your stack. Tell us what you are building and we will show you how to activate integrations, connect accounts and launch faster.',
});

const config = useRuntimeConfig();
const form = reactive({ name: '', email: '', company_website: '', audience: '' as TAudience | '', notes: '', fax_number: '' });
const errors = reactive<{ name?: string; email?: string; audience?: string; form?: string }>({});
const submitting = ref(false);
const result = ref<IBookADemoResponse | null>(null);
const promptCopied = ref(false);
const activeFaq = ref(0);
const faqSlot = (index: number) => `section-${index + 1}`;

const calendlySrc = computed(() => {
    if (!result.value?.calendly_url) {
        return '';
    }
    const url = new URL(result.value.calendly_url);
    url.searchParams.set('name', form.name.trim());
    url.searchParams.set('email', form.email.trim());
    url.searchParams.set('embed_domain', window.location.host);
    url.searchParams.set('embed_type', 'Inline');
    url.searchParams.set('hide_gdpr_banner', '1');
    return url.toString();
});

function validate() {
    errors.name = form.name.trim() ? undefined : 'Please enter your name.';
    // personal email domains are rejected by the API (isEmailPublicService), which returns WORK_EMAIL_ERROR
    errors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) ? undefined : 'Please enter a valid email.';
    errors.audience = form.audience ? undefined : "Please choose who it's for.";
    return !errors.name && !errors.email && !errors.audience;
}

async function submit() {
    errors.form = undefined;
    if (submitting.value || !validate()) {
        return;
    }
    submitting.value = true;
    try {
        result.value = await $fetch<IBookADemoResponse>(`${config.public.apiUrl}/internal/book-a-demo`, {
            method: 'POST',
            body: {
                name: form.name.trim(),
                email: form.email.trim(),
                company_website: form.company_website.trim(),
                audience: form.audience,
                notes: form.notes.trim(),
                fax_number: form.fax_number,
            },
        });
        capture('book_a_demo_submitted', { state: result.value.state, audience: form.audience });
        window.scrollTo({ top: 0 });
    } catch (error) {
        const { statusCode, data } = error as { statusCode?: number; data?: { message?: string } };
        if (data?.message === WORK_EMAIL_ERROR) {
            errors.email = WORK_EMAIL_ERROR;
        } else if (statusCode === 429) {
            errors.form = 'Too many requests. Please try again later.';
        } else {
            errors.form = 'Something went wrong. Please try again.';
        }
    } finally {
        submitting.value = false;
    }
}

function copyPrompt() {
    navigator?.clipboard?.writeText(AGENT_PROMPT).then(() => {
        promptCopied.value = true;
        setTimeout(() => (promptCopied.value = false), 2000);
    });
}
</script>
